/**
 * Permission Codes — single source of truth for role-based access control.
 *
 * Codes match the DB (V004__create_permissions.xml / V004_refreshed_permissions):
 * lowercase, dot-separated.
 *
 * Hierarchy:
 *   MODULE  → e.g. 'back_office'
 *   SCREEN  → e.g. 'back_office.bills'       (view is implicit — enabling screen grants view)
 *   ACTION  → e.g. 'back_office.bills.export'
 *
 * Usage:
 *   import { P } from '@/config/permissionCodes';
 *   hasPermission(P.BACK_OFFICE.BILLS.EXPORT)
 */

// ─── Module-level access codes ───────────────────────────────────────────────
export const MODULES = {
  BACK_OFFICE: 'back_office',
  INVENTORY: 'inventory',
  REPORTS: 'reports',
  CRM: 'crm',
  HRM: 'hrm',
  POS: 'pos',
  QR_MENU: 'qr_menu',
  KDS: 'kds',
  CAPTAIN_APP: 'captain_app',
  INTEGRATIONS: 'integrations',
  AI_INSIGHTS: 'ai_insights',
};

// ─── All permission codes, grouped by module ─────────────────────────────────
export const P = {

  // =====================================================
  // BACK OFFICE
  // =====================================================
  BACK_OFFICE: {
    ACCESS: 'back_office',

    DASHBOARD: {
      SCREEN: 'back_office.dashboard',
    },

    BILLS: {
      SCREEN: 'back_office.bills',
      EXPORT: 'back_office.bills.export',
      CHANGE_PAYMENT_MODE: 'back_office.bills.change_payment_mode',
      REPRINT: 'back_office.bills.reprint',
      CANCEL: 'back_office.bills.cancel',
      AUDIT_LOG: 'back_office.bills.audit_log',
    },

    DAY_CLOSE: {
      SCREEN: 'back_office.day_close',
      CLOSE: 'back_office.day_close.close',
      FORCE_CLOSE_SHIFT: 'back_office.day_close.force_close_shift',
    },

    MENU: {
      SCREEN: 'back_office.menu',
      ITEMS: {
        ADD: 'back_office.menu.items.add',
        EDIT: 'back_office.menu.items.edit',
        DELETE: 'back_office.menu.items.delete',
      },
      CATEGORIES: {
        ADD: 'back_office.menu.categories.add',
        EDIT: 'back_office.menu.categories.edit',
        DELETE: 'back_office.menu.categories.delete',
      },
      KITCHEN: {
        ADD: 'back_office.menu.kitchen.add',
        EDIT: 'back_office.menu.kitchen.edit',
        DELETE: 'back_office.menu.kitchen.delete',
      },
    },

    TABLES: {
      SCREEN: 'back_office.tables',
      ADD: 'back_office.tables.add',
      EDIT: 'back_office.tables.edit',
      TOGGLE: 'back_office.tables.toggle',
      DELETE: 'back_office.tables.delete',
    },

    SECTIONS: {
      ADD: 'back_office.sections.add',
      EDIT: 'back_office.sections.edit',
      TOGGLE: 'back_office.sections.toggle',
      DELETE: 'back_office.sections.delete',
    },

    SETTINGS: {
      SCREEN: 'back_office.settings',
    },
  },

  // =====================================================
  // INVENTORY
  // =====================================================
  INVENTORY: {
    ACCESS: 'inventory',

    STOCK_OVERVIEW: {
      SCREEN: 'inventory.stock_overview',
    },

    ITEMS: {
      SCREEN: 'inventory.items',
      ADD: 'inventory.items.add',
      EDIT: 'inventory.items.edit',
      UPDATE_STOCK: 'inventory.items.update_stock',
      DELETE: 'inventory.items.delete',
      CATEGORIES: {
        ADD: 'inventory.items.categories.add',
        EDIT: 'inventory.items.categories.edit',
        TOGGLE: 'inventory.items.categories.toggle',
        DELETE: 'inventory.items.categories.delete',
      },
    },

    RECIPES: {
      SCREEN: 'inventory.recipes',
      ADD: 'inventory.recipes.add',
      EDIT: 'inventory.recipes.edit',
      DUPLICATE: 'inventory.recipes.duplicate',
      DELETE: 'inventory.recipes.delete',
    },

    UOM: {
      SCREEN: 'inventory.uom',
      CATEGORIES: {
        ADD: 'inventory.uom.categories.add',
        EDIT: 'inventory.uom.categories.edit',
        DELETE: 'inventory.uom.categories.delete',
      },
      ADD: 'inventory.uom.add',
      EDIT: 'inventory.uom.edit',
      DELETE: 'inventory.uom.delete',
    },

    PURCHASE_ORDERS: {
      SCREEN: 'inventory.purchase_orders',
      CREATE: 'inventory.purchase_orders.create',
      EDIT: 'inventory.purchase_orders.edit',
      DELETE: 'inventory.purchase_orders.delete',
    },

    SUPPLIER: {
      SCREEN: 'inventory.supplier',
      ADD: 'inventory.supplier.add',
      EDIT: 'inventory.supplier.edit',
      DEACTIVATE: 'inventory.supplier.deactivate',
      DELETE: 'inventory.supplier.delete',
    },

    STOCK_ADJUSTMENT: {
      SCREEN: 'inventory.stock_adjustment',
      ADJUST: 'inventory.stock_adjustment.adjust',
    },

    WASTAGE: {
      SCREEN: 'inventory.wastage',
      LOG: 'inventory.wastage.log',
    },
  },

  // =====================================================
  // REPORTS
  // =====================================================
  REPORTS: {
    ACCESS: 'reports',
 
    SALES: {
      SCREEN: 'reports.sales',
      DAILY_EXPORT: 'reports.sales.daily.export',
      ITEMWISE_EXPORT: 'reports.sales.itemwise.export',
      CATEGORYWISE_EXPORT: 'reports.sales.categorywise.export',
      HOURLY_EXPORT: 'reports.sales.hourly.export',
    },
 
    FINANCIAL: {
      SCREEN: 'reports.financial',
      PAYMENT_EXPORT: 'reports.financial.payment.export',
      GST_DOWNLOAD: 'reports.financial.gst.download',
      DISCOUNT_EXPORT: 'reports.financial.discount.export',
      REVENUE_EXPORT: 'reports.financial.revenue.export',
    },
 
    CUSTOMERS: {
      SCREEN: 'reports.customers',
      INSIGHTS_EXPORT: 'reports.customers.insights.export',
      LOYALTY_EXPORT: 'reports.customers.loyalty.export',
      NEWREPEAT_EXPORT: 'reports.customers.newrepeat.export',
    },
 
    OPERATIONS: {
      SCREEN: 'reports.operations',
      WAITER_EXPORT: 'reports.operations.waiter.export',
      TABLE_EXPORT: 'reports.operations.table.export',
      KOT_EXPORT: 'reports.operations.kot.export',
    },
 
    INVENTORY: {
      SCREEN: 'reports.inventory',
      STOCK_EXPORT: 'reports.inventory.stock.export',
      CONSUMPTION_EXPORT: 'reports.inventory.consumption.export',
    },
  },
  

  // =====================================================
  // CRM
  // =====================================================
  CRM: {
    ACCESS: 'crm',

    CUSTOMERS: {
      SCREEN: 'crm.customers',
      ADD: 'crm.customers.add',
      EDIT: 'crm.customers.edit',
      DELETE: 'crm.customers.delete',
    },

    CAMPAIGNS: {
      SCREEN: 'crm.campaigns',
      EXPORT: 'crm.campaigns.export',
      SEND: 'crm.campaigns.send',
    },

    DISCOUNTS: {
      SCREEN: 'crm.discounts',
      ADD: 'crm.discounts.add',
      EDIT: 'crm.discounts.edit',
      DUPLICATE: 'crm.discounts.duplicate',
      TOGGLE: 'crm.discounts.toggle',
      DELETE: 'crm.discounts.delete',
    },

    LOYALTY: {
      SCREEN: 'crm.loyalty',
      EDIT: 'crm.loyalty.edit',
    },
  },

  // =====================================================
  // HRM
  // =====================================================
  HRM: {
    ACCESS: 'hrm',

    EMPLOYEES: {
      SCREEN: 'hrm.employees',
      ADD: 'hrm.employees.add',
      EDIT: 'hrm.employees.edit',
      DELETE: 'hrm.employees.delete',
    },

    ROLES: {
      SCREEN: 'hrm.roles',
      ADD: 'hrm.roles.add',
      EDIT: 'hrm.roles.edit',
      DELETE: 'hrm.roles.delete',
    },

    ATTENDANCE: {
      SCREEN: 'hrm.attendance',
      DAILY: {
        MARK: 'hrm.attendance.daily.mark',
        EDIT: 'hrm.attendance.daily.edit',
      },
      REGULARIZATION: {
        ADD: 'hrm.attendance.regularization.add',
      },
      REPORTS: {
        EXPORT: 'hrm.attendance.reports.export',
        DOWNLOAD: 'hrm.attendance.reports.download',
      },
      SETTINGS: {
        SHIFT: {
          ADD: 'hrm.attendance.settings.shift.add',
          EDIT: 'hrm.attendance.settings.shift.edit',
          DELETE: 'hrm.attendance.settings.shift.delete',
        },
        CHECKIN: 'hrm.attendance.settings.checkin',
      },
    },
  },

  // =====================================================
  // QR MENU
  // =====================================================
  QR_MENU: {
    ACCESS: 'qr_menu',

    SETTINGS: {
      SCREEN: 'qr_menu.settings',
      EDIT: 'qr_menu.settings.edit',
    },

    QR_CODES: {
      SCREEN: 'qr_menu.qr_codes',
      GENERATE_TABLE: 'qr_menu.qr_codes.generate_table',
      GENERATE_ALL: 'qr_menu.qr_codes.generate_all',
      DELETE: 'qr_menu.qr_codes.delete',
    },

    ORDERS: {
      SCREEN: 'qr_menu.orders',
    },
  },

  // =====================================================
  // POS
  // =====================================================
  POS: {
    ACCESS: 'pos',

    DINE_IN: {
      SCREEN: 'pos.dine_in',
      CREATE: 'pos.dine_in.create',
      EDIT: 'pos.dine_in.edit',
      CANCEL: 'pos.dine_in.cancel',
      MERGE: 'pos.dine_in.merge',
      SPLIT: 'pos.dine_in.split',
      PRINT_BILL: 'pos.dine_in.print_bill',
      PROMO: 'pos.dine_in.promo',
      DISCOUNT: 'pos.dine_in.discount',
      SETTLE: 'pos.dine_in.settle',
      REPRINT: 'pos.dine_in.reprint',
      NC: 'pos.dine_in.nc',
      WAITLIST: {
        ADD: 'pos.dine_in.waitlist.add',
        REMOVE: 'pos.dine_in.waitlist.remove',
        NOTIFY: 'pos.dine_in.waitlist.notify',
      },
      INCOMING: {
        VIEW: 'pos.dine_in.incoming.view',
        SEND_KITCHEN: 'pos.dine_in.incoming.send_kitchen',
        PRINT_KOT: 'pos.dine_in.incoming.print_kot',
      },
    },

    TAKEAWAY: {
      SCREEN: 'pos.takeaway',
      CREATE: 'pos.takeaway.create',
      ADD_CUSTOMER: 'pos.takeaway.add_customer',
      ADD_WAITER: 'pos.takeaway.add_waiter',
      SEND_KITCHEN: 'pos.takeaway.send_kitchen',
      PRINT_KOT: 'pos.takeaway.print_kot',
      PRINT_RECEIPT: 'pos.takeaway.print_receipt',
      PROMO: 'pos.takeaway.promo',
      DISCOUNT: 'pos.takeaway.discount',
      EBILL: 'pos.takeaway.ebill',
      SETTLE: 'pos.takeaway.settle',
      HOLD: 'pos.takeaway.hold',
      SPLIT: 'pos.takeaway.split',
      CANCEL: 'pos.takeaway.cancel',
      NC: 'pos.takeaway.nc',
    },
  },

  // =====================================================
  // KDS
  // =====================================================
  KDS: {
    ACCESS: 'kds',
  },

  // =====================================================
  // CAPTAIN APP
  // =====================================================
  CAPTAIN_APP: {
    ACCESS: 'captain_app',
  },

  // =====================================================
  // INTEGRATIONS
  // =====================================================
  INTEGRATIONS: {
    ACCESS: 'integrations',
  },

  // =====================================================
  // AI INSIGHTS
  // =====================================================
  AI_INSIGHTS: {
    ACCESS: 'ai_insights',
  },
};

export default P;
